import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  Divider,
  InlineStack,
  Page,
  Spinner,
  Text,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';

interface OrderInfo {
  id: string;
  orderNumber: string | number;
  customerName: string;
  customerEmail: string | null;
}

interface TemplateOption {
  id: string;
  name: string;
  description: string | null;
  /** Multi-signer templates only: customer + extra signers (= credits used). */
  signerCount?: number;
  triggerType?: 'MANUAL' | 'ORDER_CREATED' | 'ORDER_PAID';
  /** Linked to a product in this order. */
  forThisOrder?: boolean;
}

interface SessionSummary {
  id: string;
  templateId: string;
  signerName: string;
  signerEmail: string;
  status: string;
  createdAt: string;
  signedAt: string | null;
  /** When this contract was last emailed (re-send waits 10 minutes after it). */
  lastSentAt?: string | null;
}

/** Same as the server: one email per contract per 10 minutes. */
const RESEND_COOLDOWN_MS = 10 * 60 * 1000;

interface ManualData {
  order: OrderInfo;
  templates: TemplateOption[];
  sessions: SessionSummary[];
  /** Unfinished templates, offered when nothing is active yet. */
  drafts?: Array<{ id: string; name: string }>;
}

const STATUS_TONE: Record<string, 'success' | 'attention' | 'info' | 'warning' | 'critical'> = {
  SIGNED: 'success',
  PENDING: 'attention',
  VIEWED: 'info',
  EXPIRED: 'warning',
  DECLINED: 'critical',
};

export default function OrderManualSignPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const { request } = useApi();
  const navigate = useNavigate();

  const [data, setData] = useState<ManualData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState<Record<string, boolean>>({});
  // templateId → email the request went to (a multi-signer re-send goes to whoever's turn it is)
  const [sent, setSent] = useState<Record<string, string>>({});
  // templateId → why sending didn't work (shown in that card; the page stays usable)
  const [failed, setFailed] = useState<Record<string, { msg: string; soft: boolean }>>({});
  // Templates not linked to this order's products stay hidden until asked for.
  const [showOthers, setShowOthers] = useState(false);
  // Re-render every 30 s so "Re-send available in X min" stays current.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const load = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await request<ManualData>(`/orders/${orderId}/manual`);
      setData(res);
    } catch (err: any) {
      setError(err.message ?? 'Failed to load order');
    } finally {
      setLoading(false);
    }
  }, [orderId, request]);

  useEffect(() => { load(); }, [load]);

  const handleSend = useCallback(async (templateId: string) => {
    if (!orderId) return;
    setSending((s) => ({ ...s, [templateId]: true }));
    setFailed(({ [templateId]: _, ...rest }) => rest);
    setSent(({ [templateId]: _, ...rest }) => rest);
    try {
      const res = await request<{ signerEmail?: string }>(`/orders/${orderId}/manual/${templateId}`, { method: 'POST' });
      setSent((s) => ({ ...s, [templateId]: res?.signerEmail ?? data?.order.customerEmail ?? '' }));
      await load();
    } catch (err: any) {
      const msg: string = err.message ?? 'Failed to send';
      // The 10-minute re-send limit isn't an error: show it as a gentle notice.
      setFailed((f) => ({ ...f, [templateId]: { msg, soft: /re-send in/i.test(msg) } }));
    } finally {
      setSending((s) => ({ ...s, [templateId]: false }));
    }
  }, [orderId, request, load, data]);

  if (loading) {
    return (
      <Page title="Send for Signing">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <Spinner />
        </div>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Send for Signing">
        <Banner tone="critical">{error}</Banner>
      </Page>
    );
  }

  if (!data) return null;

  const { order, templates, sessions } = data;
  const drafts = data.drafts ?? [];
  const forOrder = templates.filter((t) => t.forThisOrder !== false);
  const others = templates.filter((t) => t.forThisOrder === false);
  // Nothing linked to this order: show everything straight away. Otherwise the
  // other templates are behind a "Show other templates" link.
  const othersVisible = forOrder.length === 0 || showOthers;
  const visible = othersVisible ? [...forOrder, ...others] : forOrder;

  // Map templateId → most recent session
  const sessionByTemplate: Record<string, SessionSummary> = {};
  for (const s of sessions) {
    if (!sessionByTemplate[s.templateId]) sessionByTemplate[s.templateId] = s;
  }

  return (
    <Page title={`Send for Signing — Order #${order.orderNumber}`}>
      <BlockStack gap="400">
        {/* Order info */}
        <Card>
          <BlockStack gap="200">
            <Text variant="headingMd" as="h2">Order #{order.orderNumber}</Text>
            <Text as="p" tone="subdued">Customer: {order.customerName}</Text>
            {order.customerEmail && (
              <Text as="p" tone="subdued">Email: {order.customerEmail}</Text>
            )}
          </BlockStack>
        </Card>

        {templates.length === 0 ? (
          <Card>
            <BlockStack gap="300">
              <Text variant="headingMd" as="h2">You don't have an active contract yet</Text>
              <Text as="p" tone="subdued">
                To send a contract for this order, first set one up and turn it on: choose a ready-made
                contract or upload your own PDF, place the signature fields, link a product and click Activate.
              </Text>
              {drafts.length > 0 && (
                <BlockStack gap="200">
                  <Text as="p" fontWeight="semibold">Finish one you started:</Text>
                  <InlineStack gap="200" wrap>
                    {drafts.map((d) => (
                      <Button key={d.id} onClick={() => navigate(`/templates/${d.id}/edit`)}>{`Finish “${d.name}”`}</Button>
                    ))}
                  </InlineStack>
                </BlockStack>
              )}
              <InlineStack>
                <Button variant="primary" onClick={() => navigate('/')}>Create a contract</Button>
              </InlineStack>
            </BlockStack>
          </Card>
        ) : (
          visible.map((template, index) => {
            const session = sessionByTemplate[template.id];
            const isSending = sending[template.id];
            const wasSent = sent[template.id];
            const failure = failed[template.id];
            const alreadySigned = session?.status === 'SIGNED';
            const cooldownMin = session?.lastSentAt
              ? Math.ceil((RESEND_COOLDOWN_MS - (now - new Date(session.lastSentAt).getTime())) / 60000)
              : 0;

            const heading = index === 0 && forOrder.length > 0 ? "For this order's products"
              : index === forOrder.length && others.length > 0
                ? (forOrder.length === 0 ? "Your active templates (none is linked to this order's products)" : 'Other active templates')
                : null;
            return (
              <BlockStack gap="200" key={template.id}>
              {heading && <Text variant="headingSm" as="h2" tone="subdued">{heading}</Text>}
              <Card>
                <BlockStack gap="300">
                  <InlineStack align="space-between" blockAlign="center">
                    <BlockStack gap="100">
                      <InlineStack gap="200" blockAlign="center">
                        <Text variant="headingMd" as="h3">{template.name}</Text>
                        {template.triggerType && template.triggerType !== 'MANUAL' && (
                          <Badge>{template.triggerType === 'ORDER_PAID' ? 'Automatic on paid orders' : 'Automatic on new orders'}</Badge>
                        )}
                      </InlineStack>
                      {template.description && (
                        <Text as="p" tone="subdued">{template.description}</Text>
                      )}
                    </BlockStack>
                    {session && (
                      <Badge tone={STATUS_TONE[session.status] ?? 'attention'}>
                        {session.status}
                      </Badge>
                    )}
                  </InlineStack>

                  {session && (
                    <>
                      <Divider />
                      <BlockStack gap="100">
                        <Text as="p" tone="subdued">
                          Sent to {session.signerEmail} on {new Date(session.createdAt).toLocaleDateString()}
                        </Text>
                        {session.signedAt && (
                          <Text as="p" tone="subdued">
                            Signed on {new Date(session.signedAt).toLocaleDateString()}
                          </Text>
                        )}
                      </BlockStack>
                    </>
                  )}

                  {wasSent && (
                    <Banner tone="success">Signing request sent to {wasSent}</Banner>
                  )}
                  {failure && (
                    <Banner tone={failure.soft ? 'warning' : 'critical'}>{failure.msg}</Banner>
                  )}
                  {template.signerCount && !session && (
                    <Text as="p" tone="subdued">
                      {template.signerCount} signers sign in order, starting with the customer. Uses {template.signerCount} signing requests.
                    </Text>
                  )}

                  <InlineStack>
                    <Button
                      variant="primary"
                      onClick={() => handleSend(template.id)}
                      loading={isSending}
                      disabled={alreadySigned || isSending || (!!session && cooldownMin > 0)}
                    >
                      {alreadySigned
                        ? 'Already Signed'
                        : session && cooldownMin > 0
                        ? `Re-send available in ${cooldownMin} min`
                        : session
                        ? 'Re-send Request'
                        : 'Send Signing Request'}
                    </Button>
                  </InlineStack>
                </BlockStack>
              </Card>
              </BlockStack>
            );
          })
        )}
        {templates.length > 0 && others.length > 0 && forOrder.length > 0 && (
          <InlineStack>
            <Button variant="plain" onClick={() => setShowOthers((v) => !v)}>
              {showOthers
                ? 'Hide other templates'
                : `Show ${others.length} other template${others.length === 1 ? '' : 's'}`}
            </Button>
          </InlineStack>
        )}
      </BlockStack>
    </Page>
  );
}
